import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";

const SOCIAL_KEYS = [
  "website",
  "instagram",
  "youtube",
  "facebook",
  "spotify",
  "soundcloud",
  "linkedin",
  "twitter",
];

const COMPLETION_FIELDS = [
  "stageName",
  "category",
  "city",
  "professionalBio",
  "genres",
  "languages",
  "priceRange",
  "coverImage",
  "portfolioMedia",
  "socialLinks",
  "mediaKitSummary",
];

export function subscribeArtistAdminProfiles(onData, onError) {
  return onSnapshot(
    collection(db, "artists"),
    (snapshot) => {
      const rows = snapshot.docs.map((entry) => normalizeArtistProfile(entry));
      onData(rows);
    },
    (error) => {
      if (typeof onError === "function") onError(error);
    }
  );
}

export async function updateArtistVerificationDecision(artistId, action, adminContext) {
  if (!artistId) {
    throw new Error("Artist id is required");
  }

  const updates = {
    verificationReviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const actor = serializeAdminContext(adminContext);
  if (actor) updates.verificationReviewedBy = actor;

  switch (String(action || "").toLowerCase()) {
    case "approve":
      updates.verifiedStatus = "approved";
      updates.verification_status = "Verified";
      updates.verificationStatus = "Verified";
      updates.status = "Approved";
      updates.isVerified = true;
      updates.verificationApprovedAt = serverTimestamp();
      break;
    case "reject":
      updates.verifiedStatus = "rejected";
      updates.verification_status = "Rejected";
      updates.verificationStatus = "Rejected";
      updates.status = "Pending";
      updates.isVerified = false;
      updates.verificationApprovedAt = null;
      break;
    case "remove":
      updates.verifiedStatus = "removed";
      updates.verification_status = "Pending";
      updates.verificationStatus = "Pending";
      updates.status = "Pending";
      updates.isVerified = false;
      updates.verificationApprovedAt = null;
      break;
    default:
      throw new Error(`Unsupported verification action: ${action}`);
  }

  await updateDoc(doc(db, "artists", artistId), updates);
}

export async function updateArtistFeaturedVisibility(artistId, isFeatured, adminContext) {
  if (!artistId) {
    throw new Error("Artist id is required");
  }

  const updates = {
    isFeatured: Boolean(isFeatured),
    featuredUpdatedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const actor = serializeAdminContext(adminContext);
  if (actor) updates.featuredUpdatedBy = actor;

  await updateDoc(doc(db, "artists", artistId), updates);
}

export async function saveArtistProfessionalProfile(artistId, formValues, adminContext) {
  if (!artistId) {
    throw new Error("Artist id is required");
  }

  const sanitized = sanitizeProfessionalProfile(formValues);
  const completion = calculateProfileCompletion(sanitized);

  const updates = {
    stageName: sanitized.stageName,
    professionalBio: sanitized.professionalBio,
    genres: sanitized.genres,
    languages: sanitized.languages,
    specialities: sanitized.specialities,
    city: sanitized.city,
    travelAvailability: sanitized.travelAvailability,
    priceRangeMin: sanitized.priceRangeMin,
    priceRangeMax: sanitized.priceRangeMax,
    socialLinks: sanitized.socialLinks,
    pastVenues: sanitized.pastVenues,
    pastEvents: sanitized.pastEvents,
    responseTimeText: sanitized.responseTimeText,
    mediaKitSummary: sanitized.mediaKitSummary,
    availabilityStatus: sanitized.availabilityStatus,
    coverImage: sanitized.coverImage,
    profileCompletionScore: completion.score,
    updatedAt: serverTimestamp(),
    professionalProfileReviewedAt: serverTimestamp(),
  };

  const actor = serializeAdminContext(adminContext);
  if (actor) updates.professionalProfileReviewedBy = actor;

  await updateDoc(doc(db, "artists", artistId), updates);
}

export async function toggleArtistPortfolioModeration(artist, mediaId, action, adminContext) {
  if (!artist?.id || !mediaId) {
    throw new Error("Artist and media id are required");
  }

  const hiddenIds = new Set(normalizeStringArray(artist.hiddenPortfolioMediaIds));
  const flaggedIds = new Set(normalizeStringArray(artist.flaggedPortfolioMediaIds));

  switch (String(action || "").toLowerCase()) {
    case "hide":
      hiddenIds.add(mediaId);
      break;
    case "unhide":
      hiddenIds.delete(mediaId);
      break;
    case "flag":
      flaggedIds.add(mediaId);
      break;
    case "unflag":
      flaggedIds.delete(mediaId);
      break;
    default:
      throw new Error(`Unsupported portfolio moderation action: ${action}`);
  }

  const updates = {
    hiddenPortfolioMediaIds: Array.from(hiddenIds),
    flaggedPortfolioMediaIds: Array.from(flaggedIds),
    portfolioModeratedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const actor = serializeAdminContext(adminContext);
  if (actor) updates.portfolioModeratedBy = actor;

  await updateDoc(doc(db, "artists", artist.id), updates);
}

export function calculateProfileCompletion(input) {
  const source = input || {};
  const presence = {
    stageName: Boolean(safeText(source.stageName)),
    category: normalizeStringArray(source.category || source.categories || source.artistCategories).length > 0,
    city: Boolean(safeText(source.city)),
    professionalBio: Boolean(safeText(source.professionalBio)),
    genres: normalizeStringArray(source.genres).length > 0,
    languages: normalizeStringArray(source.languages).length > 0,
    priceRange: Boolean(Number(source.priceRangeMin || 0) || Number(source.priceRangeMax || 0)),
    coverImage: Boolean(safeText(source.coverImage)),
    portfolioMedia:
      normalizePortfolioArray(source.portfolioImages, "image").length +
        normalizePortfolioArray(source.portfolioVideos, "video").length >
      0,
    socialLinks: Object.values(normalizeSocialLinks(source.socialLinks || source)).some(Boolean),
    mediaKitSummary: Boolean(safeText(source.mediaKitSummary)),
  };

  const completedCount = COMPLETION_FIELDS.filter((field) => presence[field]).length;
  const score = Math.round((completedCount / COMPLETION_FIELDS.length) * 100);
  const missing = COMPLETION_FIELDS.filter((field) => !presence[field]);

  return { score, missing };
}

function normalizeArtistProfile(entry) {
  const data = entry.data() || {};
  const category = normalizeStringArray(
    data.artistCategories || data.categories || data.category || data.tags
  );
  const genres = normalizeStringArray(data.genres || data.genre || data.musicGenres);
  const languages = normalizeStringArray(data.languages || data.language || data.spokenLanguages);
  const specialities = normalizeStringArray(
    data.specialities || data.specialties || data.skills || data.coreSkills
  );
  const socialLinks = normalizeSocialLinks(data.socialLinks || data);
  const hiddenPortfolioMediaIds = normalizeStringArray(data.hiddenPortfolioMediaIds);
  const flaggedPortfolioMediaIds = normalizeStringArray(data.flaggedPortfolioMediaIds);

  const portfolioImages = normalizePortfolioArray(
    data.portfolioImages ||
      data.portfolioImageUrls ||
      data.galleryImages ||
      data.workImages ||
      data.profileImages,
    "image",
    hiddenPortfolioMediaIds,
    flaggedPortfolioMediaIds
  );
  const portfolioVideos = normalizePortfolioArray(
    data.portfolioVideos ||
      data.videoPortfolio ||
      data.workVideos ||
      data.profileVideos,
    "video",
    hiddenPortfolioMediaIds,
    flaggedPortfolioMediaIds
  );

  const verificationState = normalizeVerificationState(data);
  const city = normalizeCity(data);
  const stageName = safeText(
    data.stageName ||
      data.stage_name ||
      data.artistStageName ||
      data.artistName ||
      data.displayName ||
      data.name,
    "Unnamed artist"
  );
  const artistName = safeText(data.fullName || data.name || data.artistName, stageName);
  const professionalBio = safeText(data.professionalBio || data.bio || data.about);
  const mediaKitSummary = safeText(data.mediaKitSummary || data.mediaKit || data.summary);
  const coverImage = safeText(data.coverImage || data.coverImageUrl || data.bannerImage);
  const travelAvailability = safeText(data.travelAvailability || data.travelSupport);
  const responseTimeText = safeText(data.responseTimeText || data.responseTime || data.replyTime);
  const availabilityStatus = safeText(data.availabilityStatus || data.availability);
  const priceRangeMin = toNumber(data.priceRangeMin || data.minPrice || data.priceFrom);
  const priceRangeMax = toNumber(data.priceRangeMax || data.maxPrice || data.priceTo);
  const createdAtMs = toMillis(data.createdAt);
  const updatedAtMs = toMillis(data.updatedAt);
  const pastEvents = normalizeStringArray(data.pastEvents || data.previousEvents);
  const pastVenues = normalizeStringArray(data.pastVenues || data.previousVenues);
  const completion = Number.isFinite(Number(data.profileCompletionScore))
    ? {
        score: Math.max(0, Math.min(100, Number(data.profileCompletionScore))),
        missing: calculateProfileCompletion({
          stageName,
          category,
          city,
          professionalBio,
          genres,
          languages,
          priceRangeMin,
          priceRangeMax,
          coverImage,
          portfolioImages,
          portfolioVideos,
          socialLinks,
          mediaKitSummary,
        }).missing,
      }
    : calculateProfileCompletion({
        stageName,
        category,
        city,
        professionalBio,
        genres,
        languages,
        priceRangeMin,
        priceRangeMax,
        coverImage,
        portfolioImages,
        portfolioVideos,
        socialLinks,
        mediaKitSummary,
      });

  return {
    id: entry.id,
    raw: data,
    uid: safeText(data.uid || data.user_uid || entry.id),
    artistName,
    stageName,
    category,
    categoryLabel: category.length ? category.join(", ") : "Uncategorized",
    city,
    genres,
    languages,
    specialities,
    professionalBio,
    mediaKitSummary,
    socialLinks,
    socialLinkCount: Object.values(socialLinks).filter(Boolean).length,
    travelAvailability,
    responseTimeText,
    availabilityStatus,
    priceRangeMin,
    priceRangeMax,
    priceRangeLabel: formatPriceRange(priceRangeMin, priceRangeMax),
    coverImage,
    isFeatured: Boolean(data.isFeatured),
    verificationState,
    verificationLabel: formatVerificationLabel(verificationState),
    verificationApprovedAt: data.verificationApprovedAt || null,
    portfolioImages,
    portfolioVideos,
    portfolioMedia: [...portfolioImages, ...portfolioVideos],
    portfolioCount: portfolioImages.length + portfolioVideos.length,
    hiddenPortfolioMediaIds,
    flaggedPortfolioMediaIds,
    flaggedMediaCount: [...portfolioImages, ...portfolioVideos].filter(
      (item) => item.isFlagged || item.isHidden
    ).length,
    pastEvents,
    pastVenues,
    ratingAverage: toNumber(data.ratingAverage || data.rating),
    ratingCount: toNumber(data.ratingCount || data.reviewCount),
    profileCompletionScore: completion.score,
    missingProfileFields: completion.missing,
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
    createdAtMs,
    updatedAtMs,
    searchText: [
      entry.id,
      safeText(data.uid),
      stageName,
      artistName,
      city,
      category.join(" "),
      genres.join(" "),
      languages.join(" "),
      specialities.join(" "),
      verificationState,
      Object.values(socialLinks).join(" "),
      pastEvents.join(" "),
      pastVenues.join(" "),
    ]
      .join(" ")
      .toLowerCase(),
  };
}

function sanitizeProfessionalProfile(values) {
  return {
    stageName: safeText(values?.stageName),
    professionalBio: safeText(values?.professionalBio),
    genres: normalizeStringArray(values?.genres),
    languages: normalizeStringArray(values?.languages),
    specialities: normalizeStringArray(values?.specialities),
    city: safeText(values?.city),
    travelAvailability: safeText(values?.travelAvailability),
    priceRangeMin: toNumber(values?.priceRangeMin),
    priceRangeMax: toNumber(values?.priceRangeMax),
    socialLinks: normalizeSocialLinks(values?.socialLinks || values),
    pastVenues: normalizeStringArray(values?.pastVenues),
    pastEvents: normalizeStringArray(values?.pastEvents),
    responseTimeText: safeText(values?.responseTimeText),
    mediaKitSummary: safeText(values?.mediaKitSummary),
    availabilityStatus: safeText(values?.availabilityStatus),
    coverImage: safeText(values?.coverImage),
  };
}

function normalizeVerificationState(data) {
  const direct = String(
    data.verifiedStatus ||
      data.verified_status ||
      data.verificationDecision ||
      ""
  )
    .trim()
    .toLowerCase();

  if (direct === "approved" || direct === "verified") return "approved";
  if (direct === "rejected") return "rejected";
  if (direct === "removed") return "removed";
  if (direct === "hold") return "hold";
  if (direct === "pending") return "pending";

  const legacy = String(
    data.verification_status || data.verificationStatus || data.status || ""
  )
    .trim()
    .toLowerCase();

  if (legacy === "verified" || legacy === "approved") return "approved";
  if (legacy === "rejected") return "rejected";
  if (legacy === "removed") return "removed";
  if (legacy === "hold") return "hold";
  if (legacy === "approved") return "approved";

  if (data.isVerified === true) return "approved";
  return "pending";
}

function formatVerificationLabel(state) {
  const map = {
    approved: "Verified",
    rejected: "Rejected",
    removed: "Removed",
    hold: "Hold",
    pending: "Pending",
  };
  return map[state] || "Pending";
}

function normalizeCity(data) {
  const location = data.location;
  if (location && typeof location === "object") {
    return safeText(location.city || location.label || location.address, "Unknown city");
  }

  return safeText(data.city || location || data.baseCity, "Unknown city");
}

function normalizeSocialLinks(value) {
  const next = {};
  SOCIAL_KEYS.forEach((key) => {
    next[key] = "";
  });

  if (Array.isArray(value)) {
    value.forEach((item) => {
      if (!item) return;
      const label = String(item.platform || item.type || item.key || "").trim().toLowerCase();
      const url = safeText(item.url || item.link || item.value);
      if (label && SOCIAL_KEYS.includes(label)) next[label] = url;
    });
    return next;
  }

  const source = value && typeof value === "object" ? value : {};

  SOCIAL_KEYS.forEach((key) => {
    next[key] = safeText(source[key]);
  });

  return next;
}

function normalizePortfolioArray(list, type, hiddenIds = [], flaggedIds = []) {
  if (!Array.isArray(list)) return [];

  return list
    .map((item, index) => normalizePortfolioItem(item, index, type, hiddenIds, flaggedIds))
    .filter(Boolean);
}

function normalizePortfolioItem(item, index, type, hiddenIds, flaggedIds) {
  const source = item && typeof item === "object" ? item : null;
  const url = safeText(
    source?.url ||
      source?.downloadURL ||
      source?.src ||
      source?.imageUrl ||
      source?.videoUrl ||
      item
  );

  if (!url) return null;

  const mediaId = safeText(source?.id || source?.mediaId || source?.uuid || url, `${type}-${index}`);

  return {
    id: mediaId,
    type,
    url,
    title: safeText(source?.title || source?.caption || source?.name, `${type === "image" ? "Image" : "Video"} ${index + 1}`),
    thumbUrl: safeText(source?.thumbnailUrl || source?.thumbUrl || source?.poster || (type === "image" ? url : "")),
    isHidden: hiddenIds.includes(mediaId),
    isFlagged: flaggedIds.includes(mediaId),
  };
}

function normalizeStringArray(value) {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((item) => safeText(item))
          .filter(Boolean)
      )
    );
  }

  const text = safeText(value);
  if (!text) return [];

  return Array.from(
    new Set(
      text
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

function safeText(value, fallback = "") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function toNumber(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatPriceRange(min, max) {
  const low = Number(min || 0);
  const high = Number(max || 0);

  if (low > 0 && high > 0) {
    return `Rs ${formatMoney(low)} - Rs ${formatMoney(high)}`;
  }

  if (high > 0) return `Up to Rs ${formatMoney(high)}`;
  if (low > 0) return `From Rs ${formatMoney(low)}`;
  return "Not shared";
}

function formatMoney(amount) {
  try {
    return new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));
  } catch {
    return String(amount || 0);
  }
}

function serializeAdminContext(adminContext) {
  const docId = safeText(adminContext?.docId);
  const name = safeText(adminContext?.name);
  const role = safeText(adminContext?.role);

  if (!docId && !name && !role) return null;

  return {
    ...(docId ? { docId } : {}),
    ...(name ? { name } : {}),
    ...(role ? { role } : {}),
  };
}
